import type { ArchitectureType } from "@sa-nu/core-system/src/domain/extendedModels";
import Image from "next/image";
import type { MouseEvent } from "react";

import FacilityDetailModal from "../../../../(default)/facility/_components/facility-detail-modal";
import type { FragmentType } from "../../../../../__generated__/gql";
import { graphql, useFragment } from "../../../../../__generated__/gql";
import { useInNativeApp } from "../../../../_hooks/use-in-native-app";
import { postMessageToNativeApp } from "../../../../utils/native-app";
import { Link } from "../../../ui/link";

export const SiteForHeaderFragment = graphql(`
  fragment SiteForHeader on Site {
    id
    name
    ordinalName
    slug
    area {
      slug
    }
  }
`);

// area-filtering が accommodations を accommodationsForReservation で作り替えるため、
// slug は Site 側でなく実際に描画する list 側に colocate する（宣言＝実体にする）。
// 経緯: .claude/rules/frontend/graphql-fragment-implicit-dependency.md
export const AccommodationForSiteHeaderFragment = graphql(`
  fragment AccommodationForSiteHeader on Accommodation {
    architectureType {
      seriesShortName
      size
      slug
    }
  }
`);

const FacilityForSiteHeaderFragment = graphql(`
  fragment FacilityForSiteHeader on Facility {
    id
    ...FacilityDetailModalFragment
  }
`);

export function SiteHeader({
  siteData,
  accommodationsData,
  facilitiesData,
  showChevron,
}: {
  siteData: FragmentType<typeof SiteForHeaderFragment>;
  accommodationsData: FragmentType<typeof AccommodationForSiteHeaderFragment>[];
  facilitiesData: FragmentType<typeof FacilityForSiteHeaderFragment>[];
  showChevron: boolean;
}) {
  const site = useFragment(SiteForHeaderFragment, siteData);
  const accommodations = useFragment(
    AccommodationForSiteHeaderFragment,
    accommodationsData,
  );
  const facilities = useFragment(FacilityForSiteHeaderFragment, facilitiesData);
  const { name, ordinalName } = site;
  const uniqueArchitectureTypes = getUniqueArchitectureTypes(accommodations);
  const isInNativeApp = useInNativeApp();

  // nativeapp の WebView 内では target="_blank" が機能しないため、postMessage で
  // ネイティブに委譲し WebView 画面を modal で開く（ブラウザでは従来通り別タブ遷移）。
  const openInNativeWebView =
    (path: string, title: string) => (e: MouseEvent<HTMLAnchorElement>) => {
      if (!isInNativeApp) return;
      e.preventDefault();
      postMessageToNativeApp(
        {
          type: "OpenInWebView",
          payload: {
            url: `${window.location.origin}${path}`,
            presentation: "modal",
            title,
          },
        },
        window,
      );
    };

  return (
    <div className="bg-skogecha relative flex items-center justify-between px-[20px] py-[8px] text-white">
      <div className="flex items-center gap-[8px]">
        <div className="font-roboto text-[16px] font-bold">
          {ordinalName || name}
        </div>
        {uniqueArchitectureTypes.map((at) => (
          <Link
            data-analytics-id="reservation_calendar_clicked_architecture"
            key={at.slug}
            href={`/architectures/${at.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={openInNativeWebView(`/architectures/${at.slug}`, "建築")}
            className="bg-sbeige text-skogecha rounded-full px-[8px] py-[4px] text-[10px]"
          >
            {at.seriesShortName}
          </Link>
        ))}
      </div>
      <div className="flex items-center gap-[8px]">
        {facilities.map((facility, i) => (
          <FacilityDetailModal key={i} facilityData={facility} />
        ))}
        <Link
          data-analytics-id="reservation_calendar_clicked_site_detail"
          href={`/areas/${site.area.slug}/sites/${site.slug}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={openInNativeWebView(
            `/areas/${site.area.slug}/sites/${site.slug}`,
            "拠点詳細",
          )}
          className="bg-sbeige text-skogecha rounded-sm px-[10px] py-[6px] text-[10px] font-medium hover:opacity-80"
        >
          拠点詳細
        </Link>
      </div>
      {showChevron && (
        <Image
          src="/i/reservation_form_site_arrow_icon.svg"
          width={10}
          height={5}
          alt="down"
          className="absolute bottom-[-5px] left-[27px] z-1"
        />
      )}
    </div>
  );
}

function getUniqueArchitectureTypes(
  siteAccommodations: {
    architectureType: Pick<ArchitectureType, "seriesShortName" | "slug"> &
      Partial<Pick<ArchitectureType, "size">>;
  }[],
) {
  // seriesShortName でユニーク化し、同名で size が異なる場合は非 Large の slug を優先
  const architectureTypeMap = new Map<
    string,
    { seriesShortName: string; slug: string }
  >();
  for (const a of siteAccommodations) {
    const { seriesShortName, slug, size } = a.architectureType;
    const existing = architectureTypeMap.get(seriesShortName);
    if (!existing || size !== "Large") {
      architectureTypeMap.set(seriesShortName, {
        seriesShortName,
        slug,
      });
    }
  }
  return [...architectureTypeMap.values()];
}
